import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, useDragControls, AnimatePresence } from 'framer-motion';
import { Minus, X, Square, Maximize2, Copy, ChevronLeft } from 'lucide-react';
import { useOS, AppId } from '@/lib/os-context';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { getGlassStyle, isGlassSupported, prefersReducedMotion } from '@/lib/glass-theme';
import { getLiquidTransition } from '@/lib/liquid-motion';

type SnapPosition = 'none' | 'left' | 'right' | 'top' | 'fullscreen';
type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface WindowFrameProps {
  id: AppId;
  title: string;
  children: React.ReactNode;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number | string; height: number | string };
}

export function WindowFrame({ id, title, children, initialPosition, initialSize }: WindowFrameProps) {
  const { windows, focusWindow, closeWindow, minimizeWindow, activeWindowId, toggleMaximizeWindow, updateWindowPosition, updateWindowSize, displaySettings, effectiveColorMode } = useOS();
  const windowState = windows.find((w) => w.id === id);
  const dragControls = useDragControls();
  const isMobile = useIsMobile();
  const [snapPreview, setSnapPreview] = useState<SnapPosition>('none');
  const [snapPosition, setSnapPosition] = useState<SnapPosition>('none');
  
  // Glass theme support
  const isTahoe = displaySettings.theme === 'tahoe';
  const reducedMotion = prefersReducedMotion();
  const glassEnabled = isTahoe && isGlassSupported();
  const glassStyles = glassEnabled 
    ? getGlassStyle(displaySettings.glassIntensity, displaySettings.glassVariant, effectiveColorMode)
    : {};
  
  // Use persisted position from windowState if available, otherwise use initialPosition
  const [position, setPosition] = useState({ 
    x: windowState?.position?.x ?? initialPosition?.x ?? 100, 
    y: windowState?.position?.y ?? initialPosition?.y ?? 50 
  });
  const [size, setSize] = useState({
    width: typeof windowState?.size?.width === 'number'
      ? windowState.size.width
      : typeof initialSize?.width === 'number'
        ? initialSize.width
        : 800,
    height: typeof windowState?.size?.height === 'number'
      ? windowState.size.height
      : typeof initialSize?.height === 'number'
        ? initialSize.height
        : 600,
  });
  const [isDragging, setIsDragging] = useState(false);
  const windowRef = useRef<HTMLDivElement>(null);
  const resizeStateRef = useRef<{
    direction: ResizeDirection;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    startLeft: number;
    startTop: number;
  } | null>(null);

  useEffect(() => {
    if (windowState?.position) {
      setPosition(windowState.position);
    }
    if (windowState?.size) {
      setSize(windowState.size);
    }
  }, [windowState?.position, windowState?.size]);

  // Calculate snap zones
  const checkSnapZone = useCallback((x: number, y: number): SnapPosition => {
    const screenWidth = window.innerWidth;
    const edgeThreshold = 20;

    // Left edge
    if (x <= edgeThreshold) return 'left';
    // Right edge
    if (x >= screenWidth - edgeThreshold) return 'right';
    // Top edge (fullscreen)
    if (y <= edgeThreshold) return 'fullscreen';

    return 'none';
  }, []);

  // Handle drag
  const handleDrag = useCallback((event: MouseEvent | TouchEvent | PointerEvent, info: any) => {
    const clientX = 'clientX' in event ? event.clientX : (event as TouchEvent).touches[0].clientX;
    const clientY = 'clientY' in event ? event.clientY : (event as TouchEvent).touches[0].clientY;
    
    const snap = checkSnapZone(clientX, clientY);
    setSnapPreview(snap);
  }, [checkSnapZone]);

  // Handle drag end - persist position
  const handleDragEnd = useCallback((event: MouseEvent | TouchEvent | PointerEvent, info: any) => {
    setIsDragging(false);
    
    if (snapPreview !== 'none') {
      setSnapPosition(snapPreview);
    } else if (windowRef.current) {
      // Get the actual position from the element's transform
      const rect = windowRef.current.getBoundingClientRect();
      const newPosition = { x: rect.left, y: rect.top };
      setPosition(newPosition);
      // Persist the position
      updateWindowPosition(id, newPosition);
    }
    setSnapPreview('none');
  }, [snapPreview, id, updateWindowPosition]);

  // If window is not registered or not open, don't render (MUST be after all hooks!)
  if (!windowState || !windowState.isOpen) return null;

  const isActive = activeWindowId === id;
  // On mobile, windows are always fullscreen
  const isMaximized = isMobile || windowState.isMaximized || snapPosition === 'fullscreen';

  // Get snap styles
  const getSnapStyles = () => {
    const taskbarHeight = 48;
    
    switch (snapPosition) {
      case 'left':
        return {
          x: 0,
          y: 0,
          width: '50vw',
          height: `calc(100vh - ${taskbarHeight}px)`,
        };
      case 'right':
        return {
          x: window.innerWidth / 2,
          y: 0,
          width: '50vw',
          height: `calc(100vh - ${taskbarHeight}px)`,
        };
      case 'fullscreen':
        return {
          x: 0,
          y: 0,
          width: '100vw',
          height: `calc(100vh - ${taskbarHeight}px)`,
        };
      default:
        return {};
    }
  };

  // Reset snap when double-clicking header
  const handleHeaderDoubleClick = () => {
    if (snapPosition !== 'none') {
      setSnapPosition('none');
    } else {
      toggleMaximizeWindow(id);
    }
  };

  const startResize = (direction: ResizeDirection, event: React.PointerEvent<HTMLDivElement>) => {
    if (isMobile || isMaximized || isSnapped) return;
    event.preventDefault();
    event.stopPropagation();
    focusWindow(id);

    resizeStateRef.current = {
      direction,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: size.width,
      startHeight: size.height,
      startLeft: position.x,
      startTop: position.y,
    };

    const minWidth = 340;
    const minHeight = 260;
    const maxWidth = Math.max(minWidth, window.innerWidth - 16);
    const maxHeight = Math.max(minHeight, window.innerHeight - 64);
    let latestPosition = position;
    let latestSize = size;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;

      const deltaX = moveEvent.clientX - resizeState.startX;
      const deltaY = moveEvent.clientY - resizeState.startY;
      const goesEast = resizeState.direction.includes('e');
      const goesWest = resizeState.direction.includes('w');
      const goesSouth = resizeState.direction.includes('s');
      const goesNorth = resizeState.direction.includes('n');

      let nextWidth = resizeState.startWidth + (goesEast ? deltaX : goesWest ? -deltaX : 0);
      let nextHeight = resizeState.startHeight + (goesSouth ? deltaY : goesNorth ? -deltaY : 0);
      nextWidth = Math.min(maxWidth, Math.max(minWidth, nextWidth));
      nextHeight = Math.min(maxHeight, Math.max(minHeight, nextHeight));

      let nextX = resizeState.startLeft;
      let nextY = resizeState.startTop;
      if (goesWest) {
        nextX = resizeState.startLeft + (resizeState.startWidth - nextWidth);
      }
      if (goesNorth) {
        nextY = resizeState.startTop + (resizeState.startHeight - nextHeight);
      }

      const nextPosition = {
        x: Math.min(window.innerWidth - 80, Math.max(0, nextX)),
        y: Math.min(window.innerHeight - 80, Math.max(0, nextY)),
      };
      const nextSize = { width: Math.round(nextWidth), height: Math.round(nextHeight) };
      latestPosition = nextPosition;
      latestSize = nextSize;
      setPosition(nextPosition);
      setSize(nextSize);
    };

    const stopResize = () => {
      resizeStateRef.current = null;
      updateWindowPosition(id, latestPosition);
      updateWindowSize(id, latestSize);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResize);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResize);
  };

  // Calculate drag constraints
  const windowWidth = typeof initialSize?.width === 'number' ? initialSize.width : 800;

  const snapStyles = getSnapStyles();
  const isSnapped = snapPosition !== 'none';

  return (
    <>
      {/* Snap Preview Overlay */}
      <AnimatePresence>
        {snapPreview !== 'none' && isDragging && (
          <motion.div
            className="fixed pointer-events-none z-9998"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              top: 0,
              left: snapPreview === 'right' ? '50%' : 0,
              width: snapPreview === 'fullscreen' ? '100%' : '50%',
              height: 'calc(100vh - 48px)',
            }}
          >
            <div className="w-full h-full bg-primary/20 border-2 border-primary/50 rounded-lg m-2" 
                 style={{ width: 'calc(100% - 16px)', height: 'calc(100% - 16px)' }} 
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        ref={windowRef}
        initial={{ 
          x: position.x, 
          y: position.y,
          opacity: 0,
          scale: 0.95
        }}
        animate={{ 
          opacity: windowState.isMinimized ? 0 : 1,
          scale: windowState.isMinimized ? 0.8 : 1,
          y: windowState.isMinimized ? 1000 : (isSnapped ? snapStyles.y : (isMaximized ? 0 : undefined)),
          x: isSnapped ? snapStyles.x : (isMaximized ? 0 : undefined),
          width: isSnapped ? snapStyles.width : (isMaximized ? '100vw' : size.width),
          height: isSnapped ? snapStyles.height : (isMaximized ? 'calc(100vh - 48px)' : size.height),
          borderRadius: isMaximized || isSnapped ? 0 : isTahoe ? 34 : 12,
          display: windowState.isMinimized ? 'none' : 'flex'
        }}
        transition={isTahoe ? getLiquidTransition(reducedMotion) : {
          type: "spring",
          damping: 25,
          stiffness: 300
        }}
        style={{
          zIndex: windowState.zIndex,
          position: 'absolute',
          ...glassStyles,
        }}
        className={cn(
          "flex flex-col overflow-hidden border",
          isTahoe
            ? "tahoe-window"
            : glassEnabled 
            ? "glass-highlight"
            : "bg-card",
          isActive 
            ? glassEnabled 
              ? "shadow-[0_25px_60px_rgba(0,0,0,0.5)] border-white/20" 
              : "shadow-[0_25px_60px_rgba(0,0,0,0.4)] border-primary/30" 
            : glassEnabled
              ? "shadow-xl border-white/10"
              : "shadow-xl border-border/50"
        )}
        onPointerDown={() => focusWindow(id)}
        drag={!isMaximized && !isSnapped}
        dragControls={dragControls}
        dragListener={false}
        dragMomentum={false}
        dragElastic={0}
        onDrag={handleDrag}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        dragConstraints={{
          top: 0,
          left: -windowWidth + 100,
          right: window.innerWidth - 100,
          bottom: window.innerHeight - 100
        }}
      >
        {/* Window Header */}
        <motion.div 
          className={cn(
            "h-11 border-b flex items-center justify-between px-4 select-none transition-colors",
            !isMobile && "cursor-grab active:cursor-grabbing",
            isTahoe
              ? "h-12 border-white/20 bg-white/20"
              : glassEnabled
              ? isActive 
                ? "bg-white/5 border-white/10" 
                : "bg-white/[0.02] border-white/5"
              : isActive 
                ? "bg-secondary border-border" 
                : "bg-secondary/70 border-border"
          )}
          onPointerDown={(e) => {
            e.stopPropagation();
            focusWindow(id);
            if (!isMaximized && !isMobile) {
              if (isSnapped) {
                setSnapPosition('none');
              }
              setIsDragging(true);
              dragControls.start(e);
            }
          }}
          onDoubleClick={handleHeaderDoubleClick}
        >
          {/* Mobile: Back button | Desktop: Traffic Light Buttons */}
          {isMobile ? (
            <motion.button 
              onClick={(e) => { e.stopPropagation(); closeWindow(id); }}
              aria-label="Close window and go back"
              className="flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </motion.button>
          ) : (
            <div className="flex items-center gap-2">
              <motion.button 
                onClick={(e) => { e.stopPropagation(); closeWindow(id); }}
                aria-label="Close window"
                className="w-4 h-4 rounded-full bg-[#FF5F56] hover:bg-[#ff3b30] flex items-center justify-center group transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-2.5 h-2.5 text-[#4a0002] opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.button>
              <motion.button 
                onClick={(e) => { e.stopPropagation(); minimizeWindow(id); }}
                aria-label="Minimize window"
                className="w-4 h-4 rounded-full bg-[#FFBD2E] hover:bg-[#ffcc00] flex items-center justify-center group transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Minus className="w-2.5 h-2.5 text-[#995700] opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.button>
              <motion.button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (isSnapped) {
                    setSnapPosition('none');
                  } else {
                    toggleMaximizeWindow(id); 
                  }
                }}
                aria-label={isMaximized || isSnapped ? "Restore window" : "Maximize window"}
                className="w-4 h-4 rounded-full bg-[#27C93F] hover:bg-[#00c914] flex items-center justify-center group transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {isMaximized || isSnapped ? (
                  <Copy className="w-2.5 h-2.5 text-[#006500] opacity-0 group-hover:opacity-100 transition-opacity" />
                ) : (
                  <Maximize2 className="w-2.5 h-2.5 text-[#006500] opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </motion.button>
            </div>
          )}

          {/* Window Title */}
          <div className="text-xs font-medium text-foreground/80 absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2">
            {windowState.icon && <span className="w-4 h-4 shrink-0 flex items-center justify-center">{windowState.icon}</span>}
            <span className="truncate max-w-[200px] leading-none">{title}</span>
          </div>

          {/* Snap indicator - desktop only */}
          {!isMobile && isSnapped && (
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-muted-foreground bg-secondary-foreground/10 px-2 py-0.5 rounded">
                {snapPosition === 'left' ? '◀ Left' : snapPosition === 'right' ? 'Right ▶' : 'Fullscreen'}
              </span>
            </div>
          )}

          {!isMobile && !isSnapped && <div className="w-14" />}
        </motion.div>

        {/* Window Content */}
        <div className={cn(
          "flex-1 min-h-0 overflow-auto relative",
          isTahoe ? "bg-white/55 backdrop-blur-sm" : glassEnabled ? "bg-background/80" : "bg-background"
        )}>
          {children}
        </div>

        {!isMobile && !isMaximized && !isSnapped && (
          <>
            <div className="absolute inset-x-3 top-0 h-1.5 cursor-n-resize" onPointerDown={(event) => startResize('n', event)} />
            <div className="absolute inset-x-3 bottom-0 h-1.5 cursor-s-resize" onPointerDown={(event) => startResize('s', event)} />
            <div className="absolute inset-y-3 left-0 w-1.5 cursor-w-resize" onPointerDown={(event) => startResize('w', event)} />
            <div className="absolute inset-y-3 right-0 w-1.5 cursor-e-resize" onPointerDown={(event) => startResize('e', event)} />
            <div className="absolute left-0 top-0 h-4 w-4 cursor-nw-resize" onPointerDown={(event) => startResize('nw', event)} />
            <div className="absolute right-0 top-0 h-4 w-4 cursor-ne-resize" onPointerDown={(event) => startResize('ne', event)} />
            <div className="absolute bottom-0 left-0 h-4 w-4 cursor-sw-resize" onPointerDown={(event) => startResize('sw', event)} />
            <div className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize" onPointerDown={(event) => startResize('se', event)} />
          </>
        )}
      </motion.div>
    </>
  );
}
